import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import Overlay from "web/react/components/overlay/overlay";
import ComparisonOption from "web/react/components/comparison-option/comparison-option";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { gettext } from "web/script/modules/django-i18n";
import { BuyOptionPropType } from "web/types/buy-option";
import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import browser from "web/script/utils/browser";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import { RawBranchLink } from "web/script/utils/branch-io";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import history from "web/script/utils/history";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import style from "./product-description-compare-prices.css";

const BLOCK_TYPES = {
    NO_BLOCK: "no_block",
    HARD_BLOCK: "hard_block",
    SOFT_BLOCK: "soft_block",
};

const BLOCK_TITLE_KEYS = {
    // Show all the prices for this item
    TITLE_1: "in_stock_product.compare_prices.app_overlay_block.title_1",
    // Compare prices from over 450 of stores
    TITLE_2: "in_stock_product.compare_prices.app_overlay_block.title_2",
    // Find promotions from all your favourite stores
    TITLE_3: "in_stock_product.compare_prices.app_overlay_block.title_3",
    // Find all the sales for the brands you love
    TITLE_4: "in_stock_product.compare_prices.app_overlay_block.title_4",
};

const APP_MODAL_NAME = "send-to-app-on-is-pdp-compare-prices-modal";

function ProductDescriptionComparePrice({
    allSizesSorted,
    analyticsTitle,
    buyOptions,
    shouldDisplaySizes,
    inlineVariant,
    sortedBuyOptions,
    title,
}) {
    const [comparePriceOpen, setComparePriceOpen] = useState(false);

    const [blockVariant, setBlockVariant] = useState(BLOCK_TYPES.NO_BLOCK);
    const [blockTitleVariant, setBlockTitleVariant] = useState("");
    useEffect(() => {
        if (browser.ios && !userProfiler.isLoggedIn()) {
            setBlockVariant(environment.getFeature("mg_block_is_pdp_compare_prices"));
            setBlockTitleVariant(environment.getFeature("mg_block_is_pdp_compare_prices_title"));
        } else {
            setBlockVariant(BLOCK_TYPES.NO_BLOCK);
            setBlockTitleVariant("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [browser.ios]);

    let blockTitle = "";
    if (blockVariant !== BLOCK_TYPES.NO_BLOCK && blockTitleVariant) {
        blockTitle = gettext(BLOCK_TITLE_KEYS[blockTitleVariant.toUpperCase()]);
    }

    const [blocked, setBlocked] = useState(true);
    const blockModalIsOpen = !!useOverlayWatcher(APP_MODAL_NAME);
    function onClick() {
        analytics.event(analyticsTitle ? analyticsTitle : "compare-prices", "clicked", "open");

        if (blockVariant === BLOCK_TYPES.SOFT_BLOCK && blocked) {
            setBlocked(false);
            onBlockModalOpen(true);
        } else if (blockVariant === BLOCK_TYPES.HARD_BLOCK) {
            onBlockModalOpen(true);
        } else {
            openModal();
        }
    }

    function onBlockModalClose() {
        history.popOverlay(APP_MODAL_NAME);

        // when dismissing a soft block we will open the feature
        if (blockVariant === BLOCK_TYPES.SOFT_BLOCK) {
            openModal();
        }
    }

    function onBlockModalOpen() {
        // I check the current overlay state instead of modalIsOpen
        // to get around the delay introduced by hooks
        if (history.getCurrentState().overlay !== APP_MODAL_NAME) {
            history.pushOverlay(APP_MODAL_NAME);
        }
    }

    function openModal() {
        setComparePriceOpen(true);
    }

    function closeModal() {
        setComparePriceOpen(false);
    }

    const comparePriceText = gettext("in_stock_product.compare_prices_in_stores_link", {
        store_count: sortedBuyOptions.length,
    });

    const comparePriceTextTest = gettext("in_stock_product.compare_prices_text_link");

    return (
        <React.Fragment>
            <button
                className={classnames({
                    [style.comparePricesTest]: inlineVariant,
                    [style.comparePrices]: !inlineVariant,
                })}
                onClick={onClick}
                type="button"
            >
                <span
                    className={classnames(style.comparePricesText, {
                        [style.comparePricesTestText]: !inlineVariant,
                    })}
                >
                    {title ? title : inlineVariant ? comparePriceTextTest : comparePriceText}
                </span>
                <SVGIcon className={style.icon} name="round-chevron" />
            </button>
            <SendToAppModal
                title={blockTitle}
                url={RawBranchLink.ComparePrices}
                isOpen={blockModalIsOpen}
                onClose={onBlockModalClose}
                analyticsEventLabel={"is_pdp_compare_prices"}
            />
            <Overlay
                isOpen={comparePriceOpen}
                title={gettext("in_stock_product.store_comparison_title")}
                onRequestClose={closeModal}
            >
                {sortedBuyOptions.map((linkId, index) => {
                    const buyOption = buyOptions[linkId];
                    const defaultSize = buyOption.sizes[buyOption.default_size];
                    let shippingOptions = null;
                    if (defaultSize.shipping_info) {
                        shippingOptions = defaultSize.shipping_info;
                    }
                    let freeReturn = false;
                    if (defaultSize.returns) {
                        freeReturn = defaultSize.returns.is_free;
                    }

                    return (
                        <ComparisonOption
                            key={linkId}
                            allSizesSorted={allSizesSorted}
                            buyOptionSizes={buyOption.sizes}
                            shouldDisplaySizes={shouldDisplaySizes}
                            className={style.priceBox}
                            formattedPrice={defaultSize.formatted_price}
                            freeReturn={freeReturn}
                            isAlternativeOption={index > 0}
                            isInStock={buyOption.in_stock}
                            leadUrl={defaultSize.lead_url}
                            onSale={defaultSize.is_on_sale}
                            productId={buyOption.product_id}
                            retailerName={buyOption.retailer_name}
                            salePrice={defaultSize.formatted_sale_price}
                            shippingOptions={shippingOptions}
                        />
                    );
                })}
            </Overlay>
        </React.Fragment>
    );
}

ProductDescriptionComparePrice.propTypes = {
    allSizesSorted: PropTypes.array,
    analyticsTitle: PropTypes.string,
    buyOptions: PropTypes.objectOf(BuyOptionPropType).isRequired,
    shouldDisplaySizes: PropTypes.bool,
    sortedBuyOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    inlineVariant: PropTypes.bool,
    title: PropTypes.string,
};

export default ProductDescriptionComparePrice;
